import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Eye, EyeOff, ArrowLeft, Shield } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { fadeInUp } from '@/lib/motion';

// Admin credentials
const ADMIN_USERNAME = 'admin';
const ADMIN_PASSWORD = 'admin20250824';

export default function MemberLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('請輸入帳號及密碼');
      return;
    }
    setIsLoading(true);

    // Simulate brief loading
    setTimeout(() => {
      if (username.trim() === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
        // Admin login — set session flag
        sessionStorage.setItem('hktcm_role', 'admin');
        sessionStorage.setItem('hktcm_user', username.trim());
        toast.success('管理員登入成功！歡迎回來。');
        navigate(ROUTE_PATHS.MEMBER_DASHBOARD);
      } else {
        // All other accounts — show error
        toast.error('帳號或密碼不正確，請重試。');
      }
      setIsLoading(false);
    }, 600);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Header */}
      <header className="bg-background/90 backdrop-blur-md border-b border-border px-4 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <img
              src="/images/hkcmprgu-logo-01.png"
              alt="香港中醫師權益總工會"
              className="h-10 w-auto"
            />
            <span className="text-lg font-bold text-primary hidden sm:inline">
              香港中醫師權益總工會
            </span>
          </Link>
          <Link to={ROUTE_PATHS.HOME}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              返回主頁
            </Button>
          </Link>
        </div>
      </header>

      {/* Main login area */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          className="w-full max-w-md"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">會員專區</h1>
            <p className="text-muted-foreground">請使用您的會員帳號登入</p>
          </div>

          <Card className="shadow-lg border-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <LogIn className="w-5 h-5" />
                會員登入
              </CardTitle>
              <CardDescription>
                輸入您的帳號及密碼以進入會員專區
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    帳號
                  </label>
                  <Input
                    type="text"
                    placeholder="請輸入帳號"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-muted"
                    autoComplete="username"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    密碼
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="輸入密碼"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-muted pr-10"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full inline-block" />
                      登入中...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      登入
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-center text-muted-foreground">
                  未有帳號？請聯絡本會查詢入會事宜
                </p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    asChild
                  >
                    <a href="https://wa.me/85252284182" target="_blank" rel="noopener noreferrer">
                      WhatsApp 查詢
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    asChild
                  >
                    <a href="mailto:inquiry@hkcmprgu.org.hk">
                      電郵查詢
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        © 2026 香港中醫師權益總工會。保留所有權利。
      </footer>
    </div>
  );
}
